import React, { useState, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import cs from "classnames";
import { NavLink } from "react-router-dom";

import { useRouter } from "../../../lib/hooks/useRouter";

import { getWorkplaceIcon } from "../../Workplaces/helpers/getWorkplaceInformation";
import { WorkplaceFields } from "../../../api/graphql/workplaces";
import { getWorkplaceChildren } from "../../Workplaces/helpers/getWorkplaceChildren";
import { collectAllChildren } from "../../../lib/helpers/collectWorkspaceTreeChildren";
import { Icon } from "../../shared/Icon/Icon";
import { WayFinderFormik } from "../../shared/Forms/WayFinderForm/lib/useWayFinderFormik";
import {
  MAX_DESKS_WAYFINDER,
  MAX_ROOMS_WAYFINDER,
} from "../../../lib/utils/constants";

interface Props {
  item: WorkplaceFields;
  toggleParent: (value: boolean) => void;
  handleWorkspace: (
    item: WorkplaceFields,
    type: "desks" | "rooms",
    methodType: "add" | "delete"
  ) => void;
  formik: WayFinderFormik;
  maxNumberOfRooms: boolean;
  maxNumberOfDesks: boolean;
  allToggled?: boolean;
}

export const WayFinderTreeLevel = (props: Props) => {
  const {
    item,
    toggleParent,
    handleWorkspace,
    formik,
    maxNumberOfDesks,
    maxNumberOfRooms,
    allToggled,
  } = props;

  const { pathname } = useRouter();
  const isActive = pathname.includes(item.id);

  const [toggle, setToggle] = useState(() => isActive);

  useEffect(() => {
    if (allToggled !== undefined) {
      setToggle(allToggled);
    }
  }, [allToggled]);

  const icon = getWorkplaceIcon(item.__typename);
  const workplaceChildren = getWorkplaceChildren(item);
  const hasChildren = workplaceChildren.some((item) => item && item.length > 0);

  const allTargetTypeDescendants = useMemo(() => {
    const descendants = [item, ...collectAllChildren(item)].filter(
      (child) => child.__typename === "Room" || child.__typename === "Desk"
    );
    return descendants;
  }, [item]);

  const isParentSelected = useMemo(() => {
    return allTargetTypeDescendants.some((descendant) =>
      formik.values[descendant.__typename === "Desk" ? "desks" : "rooms"].some(
        (formikItem) => formikItem.id === descendant.id
      )
    );
  }, [allTargetTypeDescendants, formik.values]);

  const handleToggle = () => {
    setToggle((prev) => !prev);
  };

  const handleParentWorkspaceClick = () => {
    const deskDescendants: WorkplaceFields[] = [];
    const roomDescendants: WorkplaceFields[] = [];

    allTargetTypeDescendants.forEach((descendant) => {
      if (descendant.__typename === "Desk") {
        deskDescendants.push(descendant);
      } else if (descendant.__typename === "Room") {
        roomDescendants.push(descendant);
      }
    });

    // desk descendants
    if (deskDescendants.length > 0) {
      const currentDesks = formik.values.desks;
      const hasSelectedDesks = deskDescendants.some((desk) =>
        currentDesks.some((formikDesk) => formikDesk.id === desk.id)
      );

      if (!hasSelectedDesks) {
        const newDesks = deskDescendants.filter(
          (desk) =>
            !currentDesks.some((formikDesk) => formikDesk.id === desk.id)
        );

        const availableSlots = MAX_DESKS_WAYFINDER - currentDesks.length;

        if (availableSlots <= 0) {
          toast.warn("You have reached the maximum number allowed for desks");
          return;
        }

        const desksToAdd = newDesks.slice(0, availableSlots);
        formik.setFieldValue("desks", [...currentDesks, ...desksToAdd]);

        if (newDesks.length > availableSlots) {
          toast.warn("You have reached the maximum number allowed for desks");
        }
      } else {
        const deskIdsToRemove = deskDescendants.map((desk) => desk.id);
        const updatedDesks = currentDesks.filter(
          (desk) => !deskIdsToRemove.includes(desk.id)
        );
        formik.setFieldValue("desks", updatedDesks);
      }
    }

    // room descendants
    if (roomDescendants.length > 0) {
      const currentRooms = formik.values.rooms;
      const hasSelectedRooms = roomDescendants.some((room) =>
        currentRooms.some((formikRoom) => formikRoom.id === room.id)
      );

      if (!hasSelectedRooms) {
        const newRooms = roomDescendants.filter(
          (room) =>
            !currentRooms.some((formikRoom) => formikRoom.id === room.id)
        );

        const availableSlots = MAX_ROOMS_WAYFINDER - currentRooms.length;

        if (availableSlots <= 0) {
          toast.warn("You have reached the maximum number allowed for rooms");
          return;
        }

        const roomsToAdd = newRooms.slice(0, availableSlots);
        formik.setFieldValue("rooms", [...currentRooms, ...roomsToAdd]);

        if (newRooms.length > availableSlots) {
          toast.warn("You have reached the maximum number allowed for rooms");
        }
      } else {
        const roomIdsToRemove = roomDescendants.map((room) => room.id);
        const updatedRooms = currentRooms.filter(
          (room) => !roomIdsToRemove.includes(room.id)
        );
        formik.setFieldValue("rooms", updatedRooms);
      }
    }
  };

  const handleWorkspaceClick = () => {
    if (["Desk", "Room"].includes(item.__typename)) {
      let typeOfMethod: "add" | "delete" = formik.values[
        item.__typename === "Desk" ? "desks" : "rooms"
      ].some((formikItem) => formikItem.id === item.id)
        ? "delete"
        : "add";

      if (
        typeOfMethod === "add" &&
        item.__typename === "Desk" &&
        maxNumberOfDesks
      ) {
        return toast.warn(
          "You have reached the maximum number allowed for desks"
        );
      }

      if (
        typeOfMethod === "add" &&
        item.__typename === "Room" &&
        maxNumberOfRooms
      ) {
        return toast.warn(
          "You have reached the maximum number allowed for rooms"
        );
      }

      handleWorkspace(
        item,
        item.__typename === "Desk" ? "desks" : "rooms",
        typeOfMethod
      );
    } else {
      handleParentWorkspaceClick();
    }
  };

  const classes = cs("WorkplacesTreeLevel", {
    "WorkplacesTreeLevel--open": toggle,
    "WorkplacesTreeLevel--active": isActive,
    "WorkplacesTreeLevel--space": !hasChildren,
  });

  const subLevelClasses = cs("WorkplacesTreeLevel__sub-level", {
    "WorkplacesTreeLevel__sub-level--open": toggle,
  });

  return (
    <>
      <div className={classes}>
        <div className="WorkplacesTreeLevel__wrapper">
          {hasChildren && (
            <span
              onClick={handleToggle}
              className="WorkplacesTreeLevel__toggle"
            ></span>
          )}
          <NavLink
            className="WorkplacesTreeLevel__link"
            onClick={handleWorkspaceClick}
            to={"#"}
          >
            <div className="WorkplacesTreeLevel__icon">
              <Icon icon={icon} />
            </div>
            <h3 className="WorkplacesTreeLevel__name">{item.name}</h3>
          </NavLink>
          {item.__typename === "Desk" || item.__typename === "Room" ? (
            <>
              <div
                className="WorkplacesTreeLevel__status"
                onClick={handleWorkspaceClick}
              >
                <Icon
                  icon={
                    formik.values[
                      item.__typename === "Desk" ? "desks" : "rooms"
                    ].some((formikItem) => formikItem.id === item.id)
                      ? "checkbox-full"
                      : "checkbox-empty"
                  }
                />
              </div>
            </>
          ) : (
            allTargetTypeDescendants.length > 1 && (
              <div
                className="WorkplacesTreeLevel__status"
                onClick={handleWorkspaceClick}
              >
                <Icon
                  icon={isParentSelected ? "checkbox-full" : "checkbox-empty"}
                />
              </div>
            )
          )}
        </div>
      </div>
      {hasChildren && (
        <div className={subLevelClasses}>
          {workplaceChildren.map((child) => {
            return child
              ?.filter((workplace) => workplace.__typename !== "Wayfinder")
              .map((workplace) => (
                <WayFinderTreeLevel
                  item={workplace}
                  key={`${workplace.id}-tree-level`}
                  handleWorkspace={handleWorkspace}
                  formik={formik}
                  toggleParent={(value) => {
                    setToggle(value);
                    toggleParent(value);
                  }}
                  maxNumberOfRooms={maxNumberOfRooms}
                  maxNumberOfDesks={maxNumberOfDesks}
                  allToggled={allToggled}
                />
              ));
          })}
        </div>
      )}
    </>
  );
};
